import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { HubDataTable, type HubDataTableColumn } from "@/components/hub/HubDataTable";
import { RepAgencyDetailLink } from "@/components/inside-sales/RepAgencyDetailLink";
import { apiFetch } from "@/lib/api";
import { hubModuleAllowsAdminSettings } from "@/lib/subapp-access";
import { useIntegratorMe } from "@/lib/me-query";
import { Button } from "@/components/ui/Button";
import { Card, CardBody, CardHeader, CardTitle } from "@/components/ui/Card";
import { Input, Label } from "@/components/ui/Input";
import type { SortDirection } from "@/components/ui/SortableTableHeader";
import { PageTitleWithHelp } from "@/components/ui/title-help";

import { listCommissionAdjustments, money } from "./commissionsApi";

type AdjustmentRow = Record<string, unknown>;

type SortKey = "run_month" | "rep" | "kind" | "amount" | "commission" | "notes" | "rca";

const SELECT_CLASS =
  "mt-1 block h-10 w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm";

function formatDate(value: unknown): string {
  if (value == null || String(value).trim() === "") {
    return "—";
  }
  const text = String(value).slice(0, 10);
  const parsed = new Date(`${text}T12:00:00`);
  if (Number.isNaN(parsed.getTime())) {
    return text;
  }
  return parsed.toLocaleDateString("en-US", { month: "short", year: "numeric" });
}

function repDisplayName(row: AdjustmentRow): string {
  const resolved = row.resolved_rep_name;
  const agency = row.rep_agency_name;
  if (resolved != null && String(resolved).trim() !== "") {
    return String(resolved);
  }
  if (agency != null && String(agency).trim() !== "") {
    return String(agency);
  }
  return "—";
}

function parseNumericField(value: unknown): number | null {
  if (value == null || value === "") {
    return null;
  }
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

function compareNullableStrings(left: string | null, right: string | null): number {
  const leftEmpty = left == null || left.trim() === "";
  const rightEmpty = right == null || right.trim() === "";
  if (leftEmpty && rightEmpty) {
    return 0;
  }
  if (leftEmpty) {
    return 1;
  }
  if (rightEmpty) {
    return -1;
  }
  return left.localeCompare(right, undefined, { sensitivity: "base" });
}

function compareNullableNumbers(left: number | null, right: number | null): number {
  if (left == null && right == null) {
    return 0;
  }
  if (left == null) {
    return 1;
  }
  if (right == null) {
    return -1;
  }
  return left - right;
}

function compareRows(
  left: AdjustmentRow,
  right: AdjustmentRow,
  sortKey: SortKey,
  sortDir: SortDirection,
): number {
  let cmp = 0;
  switch (sortKey) {
    case "run_month":
      cmp = compareNullableStrings(
        left.run_month != null ? String(left.run_month) : null,
        right.run_month != null ? String(right.run_month) : null,
      );
      break;
    case "rep":
      cmp = compareNullableStrings(repDisplayName(left), repDisplayName(right));
      break;
    case "kind":
      cmp = compareNullableStrings(
        left.kind != null ? String(left.kind) : null,
        right.kind != null ? String(right.kind) : null,
      );
      break;
    case "amount":
      cmp = compareNullableNumbers(parseNumericField(left.amount), parseNumericField(right.amount));
      break;
    case "commission":
      cmp = compareNullableNumbers(
        parseNumericField(left.commission_amount),
        parseNumericField(right.commission_amount),
      );
      break;
    case "notes":
      cmp = compareNullableStrings(
        left.notes != null ? String(left.notes) : null,
        right.notes != null ? String(right.notes) : null,
      );
      break;
    case "rca":
      cmp = compareNullableStrings(
        left.rca != null ? String(left.rca) : null,
        right.rca != null ? String(right.rca) : null,
      );
      break;
    default: {
      const _exhaustive: never = sortKey;
      return _exhaustive;
    }
  }
  if (cmp === 0) {
    cmp = Number(left.id) - Number(right.id);
  }
  return sortDir === "asc" ? cmp : -cmp;
}

const ADJUSTMENT_COLUMNS: HubDataTableColumn<AdjustmentRow, SortKey>[] = [
  {
    id: "run_month",
    header: "Month",
    sortKey: "run_month",
    overflow: "nowrap",
    sortValue: (row) => (row.run_month != null ? String(row.run_month) : ""),
    cell: (row) => formatDate(row.run_month),
  },
  {
    id: "rep",
    header: "Rep",
    sortKey: "rep",
    sortValue: (row) => repDisplayName(row),
    cell: (row) => {
      const repAgencyId = row.rep_agency_id != null ? Number(row.rep_agency_id) : null;
      const repName = repDisplayName(row);
      return (
        <RepAgencyDetailLink
          repAgencyId={repAgencyId}
          repAgencyName={repName === "—" ? null : repName}
        />
      );
    },
  },
  {
    id: "kind",
    header: "Kind",
    sortKey: "kind",
    overflow: "nowrap",
    sortValue: (row) => (row.kind != null ? String(row.kind) : ""),
    cell: (row) => <span className="capitalize">{String(row.kind ?? "—")}</span>,
  },
  {
    id: "amount",
    header: "Amount",
    sortKey: "amount",
    align: "right",
    overflow: "nowrap",
    sortValue: (row) => parseNumericField(row.amount) ?? "",
    cell: (row) => money(row.amount as string | number | null),
  },
  {
    id: "commission",
    header: "Commission",
    sortKey: "commission",
    align: "right",
    overflow: "nowrap",
    sortValue: (row) => parseNumericField(row.commission_amount) ?? "",
    cell: (row) => money(row.commission_amount as string | number | null),
  },
  {
    id: "notes",
    header: "Notes",
    sortKey: "notes",
    overflow: "truncate",
    sortValue: (row) => (row.notes != null ? String(row.notes) : ""),
    cell: (row) => String(row.notes ?? "—"),
  },
  {
    id: "rca",
    header: "RCA",
    sortKey: "rca",
    overflow: "truncate",
    sortValue: (row) => (row.rca != null ? String(row.rca) : ""),
    cell: (row) => String(row.rca ?? "—"),
  },
];

function AddAdjustmentModal({
  onCancel,
  onCreated,
}: {
  onCancel: () => void;
  onCreated: () => void;
}) {
  const [runMonth, setRunMonth] = useState("");
  const [repName, setRepName] = useState("");
  const [amount, setAmount] = useState("");
  const [commission, setCommission] = useState("");
  const [notes, setNotes] = useState("");
  const [kind, setKind] = useState<"regular" | "bonus">("regular");
  const [rca, setRca] = useState("");

  const createMutation = useMutation({
    mutationFn: () =>
      apiFetch("/api/commissions/adjustments", {
        method: "POST",
        body: {
          run_month: `${runMonth}-01`,
          rep_agency_name: repName || null,
          amount: amount.trim() === "" ? null : Number.parseFloat(amount),
          commission_amount: commission.trim() === "" ? null : Number.parseFloat(commission),
          notes: notes || null,
          kind,
          rca: rca || null,
        },
      }),
    onSuccess: () => {
      onCreated();
    },
  });

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4"
      onClick={(event) => {
        if (event.target === event.currentTarget && !createMutation.isPending) {
          onCancel();
        }
      }}
    >
      <div className="w-full max-w-lg rounded-lg bg-white shadow-xl">
        <div className="border-b border-slate-200 px-6 py-4">
          <h2 className="text-base font-semibold text-slate-900">Add adjustment</h2>
        </div>
        <div className="grid gap-3 px-6 py-4 sm:grid-cols-2">
          <div>
            <Label htmlFor="adj-month">Month</Label>
            <Input
              id="adj-month"
              type="month"
              className="mt-1"
              value={runMonth}
              onChange={(event) => setRunMonth(event.target.value)}
            />
          </div>
          <div>
            <Label htmlFor="adj-rep">Rep agency name</Label>
            <Input
              id="adj-rep"
              className="mt-1"
              value={repName}
              onChange={(event) => setRepName(event.target.value)}
            />
          </div>
          <div>
            <Label htmlFor="adj-kind">Kind</Label>
            <select
              id="adj-kind"
              className={SELECT_CLASS}
              value={kind}
              onChange={(event) => setKind(event.target.value as "regular" | "bonus")}
            >
              <option value="regular">Regular</option>
              <option value="bonus">Bonus</option>
            </select>
          </div>
          <div>
            <Label htmlFor="adj-amount">Amount</Label>
            <Input
              id="adj-amount"
              className="mt-1"
              value={amount}
              onChange={(event) => setAmount(event.target.value)}
            />
          </div>
          <div>
            <Label htmlFor="adj-comm">Commission</Label>
            <Input
              id="adj-comm"
              className="mt-1"
              value={commission}
              onChange={(event) => setCommission(event.target.value)}
            />
          </div>
          <div>
            <Label htmlFor="adj-notes">Notes</Label>
            <Input
              id="adj-notes"
              className="mt-1"
              value={notes}
              onChange={(event) => setNotes(event.target.value)}
            />
          </div>
          <div className="sm:col-span-2">
            <Label htmlFor="adj-rca">Root cause</Label>
            <Input
              id="adj-rca"
              className="mt-1"
              value={rca}
              onChange={(event) => setRca(event.target.value)}
            />
          </div>
          {createMutation.isError ? (
            <p className="sm:col-span-2 text-sm text-red-700">
              {createMutation.error instanceof Error
                ? createMutation.error.message
                : "Could not add adjustment."}
            </p>
          ) : null}
        </div>
        <div className="flex justify-end gap-2 border-t border-slate-200 px-6 py-4">
          <Button type="button" variant="secondary" onClick={onCancel} disabled={createMutation.isPending}>
            Cancel
          </Button>
          <Button
            type="button"
            onClick={() => createMutation.mutate()}
            disabled={createMutation.isPending || runMonth.trim() === ""}
          >
            {createMutation.isPending ? "Adding…" : "Add adjustment"}
          </Button>
        </div>
      </div>
    </div>
  );
}

export function CommissionAdjustmentsPage() {
  const queryClient = useQueryClient();
  const meQuery = useIntegratorMe();
  const canEdit = hubModuleAllowsAdminSettings(meQuery.data, "commissions");
  const [filterRunMonth, setFilterRunMonth] = useState("");
  const [showAddModal, setShowAddModal] = useState(false);
  const [sortKey, setSortKey] = useState<SortKey>("run_month");
  const [sortDir, setSortDir] = useState<SortDirection>("desc");

  const listQuery = useQuery({
    queryKey: ["commissions", "adjustments", filterRunMonth || "all"],
    queryFn: () =>
      listCommissionAdjustments(filterRunMonth ? `${filterRunMonth}-01` : undefined),
  });

  const rows = listQuery.data?.rows ?? [];

  const sortedRows = useMemo(() => {
    return [...rows].sort((left, right) => compareRows(left, right, sortKey, sortDir));
  }, [rows, sortKey, sortDir]);

  const hasActiveFilter = filterRunMonth.trim() !== "";

  function toggleSort(nextKey: SortKey): void {
    if (sortKey === nextKey) {
      setSortDir((current) => (current === "asc" ? "desc" : "asc"));
      return;
    }
    setSortKey(nextKey);
    setSortDir(nextKey === "rep" || nextKey === "notes" || nextKey === "rca" ? "asc" : "desc");
  }

  function handleAdjustmentCreated(): void {
    setShowAddModal(false);
    void queryClient.invalidateQueries({ queryKey: ["commissions", "adjustments"] });
  }

  return (
    <div className="space-y-6">
      <PageTitleWithHelp hoverHint="Manual inputs and corrections flow into the next compute for that month.">
        Adjustments and corrections
      </PageTitleWithHelp>

      {!canEdit ? (
        <p className="text-sm text-muted-foreground">
          View only — commission module administrators can add manual adjustments.
        </p>
      ) : null}

      <Card>
        <CardHeader>
          <CardTitle>Filters</CardTitle>
        </CardHeader>
        <CardBody className="grid gap-3 md:grid-cols-3">
          <div>
            <Label htmlFor="adj-filter-month">Run month</Label>
            <Input
              id="adj-filter-month"
              type="month"
              className="mt-1"
              value={filterRunMonth}
              onChange={(event) => setFilterRunMonth(event.target.value)}
            />
          </div>
          {hasActiveFilter && rows.length > 0 ? (
            <p className="flex items-end pb-2 text-sm text-slate-600 md:col-span-2">
              Showing {String(rows.length)} adjustment{rows.length === 1 ? "" : "s"} for{" "}
              {formatDate(`${filterRunMonth}-01`)}.
            </p>
          ) : null}
        </CardBody>
      </Card>

      <HubDataTable
        title={
          <>
            Adjustments
            {rows.length > 0 ? (
              <span className="font-normal text-slate-600"> ({String(rows.length)})</span>
            ) : null}
          </>
        }
        headerExtra={
          canEdit ? (
            <Button type="button" size="sm" onClick={() => setShowAddModal(true)}>
              Add adjustment
            </Button>
          ) : null
        }
        columns={ADJUSTMENT_COLUMNS}
        rows={sortedRows}
        rowKey={(row) => String(row.id)}
        sortKey={sortKey}
        sortDir={sortDir}
        onSort={toggleSort}
        loading={listQuery.isPending}
        loadingMessage="Loading adjustments…"
        error={
          listQuery.isError
            ? listQuery.error instanceof Error
              ? listQuery.error.message
              : "Could not load adjustments."
            : null
        }
        empty="No manual adjustments recorded."
        isFiltered={hasActiveFilter}
        filteredEmpty="No adjustments match this run month."
        tableClassName="min-w-[48rem]"
      />

      {showAddModal ? (
        <AddAdjustmentModal onCancel={() => setShowAddModal(false)} onCreated={handleAdjustmentCreated} />
      ) : null}
    </div>
  );
}
